import React, { createContext, useContext, useState, ReactNode } from "react";
import { supabase } from "src/lib/supabaseClient";
import { useAuth } from "./AuthContext";
import { useToast } from "src/components/ui/UseToast";
import { educationLevels } from "src/constants/educationLevels";
import { learningGoals as learningGoalsList } from "src/constants/learningGoals";

interface OnboardingContextType {
  currentStep: number;
  totalSteps: number;
  nextStep: () => void;
  prevStep: () => void;
  completeOnboarding: () => Promise<void>;
  // Personal information
  firstName: string;
  setFirstName: (name: string) => void;
  lastName: string;
  setLastName: (name: string) => void;
  dateOfBirth: string | null;
  setDateOfBirth: (date: string | null) => void;
  // Education level
  educationLevel: string;
  setEducationLevel: (level: string) => void;
  // Learning goals
  learningGoals: string[];
  setLearningGoals: (goals: string[]) => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined
);

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
}

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3; // Updated to remove subject selection step

  // Step 3: Personal information
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<string | null>(null);

  // Step 1: Education level
  const [educationLevel, setEducationLevel] = useState("");

  // Step 2: Learning goals
  const [learningGoals, setLearningGoals] = useState<string[]>([]);

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeOnboarding = async () => {
    if (!user) {
      toast({
        title: "Not logged in",
        description: "You must be logged in to complete onboarding",
        variant: "destructive",
      });
      return;
    }

    try {
      // Map educationLevel id to label
      const educationLevelLabel = educationLevels.find(l => l.id === educationLevel)?.label || educationLevel;
      // Map learningGoals ids to labels
      const learningGoalsLabels = learningGoals.map(
        goalId => learningGoalsList.find(g => g.id === goalId)?.label || goalId
      );
      // Save all onboarding data
      const { error } = await supabase.from("candidate_student").upsert({
        id: user.id,
        email: user.email,
        first_name: firstName,
        last_name: lastName,
        date_of_birth: dateOfBirth,
        education_level: educationLevelLabel,
        learning_goals: learningGoalsLabels,
        onboarding_completed: true,
      });

      if (error) throw error;

      toast({
        title: "Onboarding Complete",
        description: "Your profile has been set up successfully!",
      });

      // Don't navigate here - let the calling component handle navigation
      // This prevents conflicts with the process modal's redirect logic
    } catch (error: any) {
      console.error("Error completing onboarding:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to complete onboarding",
        variant: "destructive",
      });
    }
  };

  const value = {
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    completeOnboarding,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    dateOfBirth,
    setDateOfBirth,
    educationLevel,
    setEducationLevel,
    learningGoals,
    setLearningGoals,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
